import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CONFIG, formatInviteCode, profanityError } from '@h2h/shared';
import { AppShell, Content } from '../../components/ui/AppShell';
import { DesktopColumns } from '../../components/ui/DesktopColumns';
import { Avatar, MiniLeagueMark } from '../../components/ui/bits';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  LogOut,
  MoreHorizontal,
  Palette,
  Pencil,
  Share2,
  Trash2
} from '../../components/ui/icons';
import { useHaptics } from '../../hooks/useHaptics';
import { useOpenProfile } from '../../hooks/useOpenProfile';
import { useAuth } from '../../context/AuthContext';
import {
  useDeleteMiniLeague,
  useKickMiniLeagueMember,
  useLeagueRounds,
  useLeagues,
  useLeaveMiniLeague,
  useMiniLeague,
  useMiniLeagueStandings,
  useRenameMiniLeague,
  useUpdateMiniLeague
} from '../../hooks/useQueries';
import { identityFor } from '../../data/leagues';
import { shareMiniLeagueInvite } from './miniLeagueHelpers';
import { ColorPicker, IconPicker, LeagueNav } from './parts';
import { cn } from '../../lib/cn';

export default function Manage() {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const openProfile = useOpenProfile();
  const { id } = useParams<{ id: string }>();
  const { uid } = useAuth();
  const { data: mini, isLoading } = useMiniLeague(id);
  const { data: rows } = useMiniLeagueStandings(uid, mini);
  const { data: leagues } = useLeagues();
  const { data: rounds } = useLeagueRounds(mini?.leagueId);

  const rename = useRenameMiniLeague(uid);
  const update = useUpdateMiniLeague(uid);
  const kick = useKickMiniLeagueMember(uid);
  const del = useDeleteMiniLeague(uid);
  const leave = useLeaveMiniLeague(uid);

  const [showAll, setShowAll] = useState(false);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [editName, setEditName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [editLook, setEditLook] = useState(false);
  const [iconDraft, setIconDraft] = useState('');
  const [colorDraft, setColorDraft] = useState('');
  const [copied, setCopied] = useState(false);

  const roundLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rounds ?? []) map.set(r.roundId, r.label);
    return map;
  }, [rounds]);

  if (isLoading || !mini) {
    return (
      <AppShell>
        <Content>
          <LeagueNav label="Back" onBack={() => navigate('/leagues')} />
          <div className="pt-20 text-center text-[14px] font-medium text-ink2">Loading…</div>
        </Content>
      </AppShell>
    );
  }

  const isOwner = mini.ownerId === uid;
  const leagueName =
    leagues?.find((l) => l.leagueId === mini.leagueId)?.name ?? identityFor(mini.leagueId)?.name ?? 'League';

  const rowFor = (memberId: string) => rows?.find((r) => r.userId === memberId);
  const ordered = [mini.ownerId, ...mini.memberIds.filter((m) => m !== mini.ownerId)];
  const visible = showAll ? ordered : ordered.slice(0, 4);

  const copyCode = async () => {
    impact('light');
    try {
      await navigator.clipboard.writeText(mini.inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* unavailable */
    }
  };

  const shareCode = async () => {
    impact('light');
    await shareMiniLeagueInvite(mini);
  };

  const startRename = () => {
    setNameDraft(mini.name);
    setEditName(true);
    setEditLook(false);
  };
  const saveName = () => {
    if (!nameDraft.trim() || profanityError(nameDraft, 'league name')) return;
    rename.mutate({ miniLeagueId: mini.miniLeagueId, name: nameDraft.trim() }, { onSuccess: () => setEditName(false) });
  };
  const startLook = () => {
    setIconDraft(mini.icon);
    setColorDraft(mini.color);
    setEditLook(true);
    setEditName(false);
  };
  const saveLook = () => {
    update.mutate(
      { miniLeagueId: mini.miniLeagueId, icon: iconDraft, color: colorDraft },
      { onSuccess: () => setEditLook(false) }
    );
  };
  const onDelete = () => {
    if (!window.confirm(`Delete "${mini.name}"? This can’t be undone.`)) return;
    impact('warning');
    del.mutate(mini.miniLeagueId, { onSuccess: () => navigate('/leagues', { replace: true }) });
  };
  const onLeave = () => {
    if (!window.confirm(`Leave "${mini.name}"?`)) return;
    impact('warning');
    leave.mutate(mini.miniLeagueId, { onSuccess: () => navigate('/leagues', { replace: true }) });
  };

  return (
    <AppShell>
      <Content>
        <LeagueNav
          label={mini.name}
          onBack={() => navigate(`/leagues/${mini.miniLeagueId}`)}
          right={<span className="pr-1.5 text-[14px] font-bold tracking-snug text-ink">Manage</span>}
        />

        <DesktopColumns
          asideWidth="360px"
          main={
            <>
        {/* league head */}
        <div className="flex items-center gap-[13px] pt-4">
          <MiniLeagueMark icon={mini.icon} color={mini.color} size={54} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[19px] font-extrabold tracking-tight text-ink">{mini.name}</div>
            <div className="truncate text-[12px] font-medium text-ink2">{leagueName} · This season</div>
          </div>
          {isOwner && (
            <button
              type="button"
              onClick={() => {
                impact('light');
                startRename();
              }}
              className="tap flex items-center gap-1.5 rounded-full bg-fill px-3.5 py-2"
            >
              <Pencil width={14} height={14} strokeWidth={2.2} className="text-ink" />
              <span className="text-[13px] font-bold text-ink">Edit</span>
            </button>
          )}
        </div>

        {/* join code row */}
        <div className="pt-[18px]">
          <div className="flex items-center gap-3 rounded-[18px] border border-black/7 bg-card p-[14px_15px]">
            <div className="flex-1">
              <div className="text-[11px] font-bold uppercase tracking-wide text-ink2">Join code</div>
              <div className="num mt-0.5 text-[20px] font-bold tracking-wide text-ink">
                {formatInviteCode(mini.inviteCode)}
              </div>
            </div>
            <button
              type="button"
              aria-label="Copy code"
              onClick={copyCode}
              className="tap grid h-10 w-10 place-items-center rounded-[12px] bg-fill"
            >
              {copied ? (
                <Check width={18} height={18} strokeWidth={2.4} className="text-accent-ink" />
              ) : (
                <Copy width={18} height={18} strokeWidth={2} className="text-ink" />
              )}
            </button>
            <button
              type="button"
              aria-label="Share code"
              onClick={shareCode}
              className="tap grid h-10 w-10 place-items-center rounded-[12px] bg-night"
            >
              <Share2 width={18} height={18} strokeWidth={2.2} className="text-white" />
            </button>
          </div>
        </div>

        {/* members */}
        <div className="flex items-baseline justify-between pb-2 pt-6">
          <span className="text-[15px] font-extrabold tracking-tight text-ink">Members</span>
          <span className="text-[12px] font-semibold text-ink2">
            {mini.memberIds.length} {mini.memberIds.length === 1 ? 'friend' : 'friends'}
          </span>
        </div>

        <div className="overflow-hidden rounded-[20px] border border-black/7 bg-card">
          {visible.map((memberId, i) => {
            const r = rowFor(memberId);
            const owner = memberId === mini.ownerId;
            const you = memberId === uid;
            const joinId = mini.members?.[memberId]?.joinedRoundId ?? null;
            const sub = owner
              ? 'Created this league'
              : joinId && roundLabel.get(joinId)
                ? `Joined ${roundLabel.get(joinId)}`
                : 'Member';
            return (
              <div key={memberId}>
                {i > 0 && <div className="ml-[62px] h-px bg-black/5" />}
                <div className="flex items-center gap-3 p-[12px_14px]">
                  <button
                    type="button"
                    onClick={() => openProfile(memberId, you)}
                    className="tap flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    <Avatar name={r?.displayName ?? 'Player'} tone={r?.avatarTone ?? 0} size={36} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[14px] font-bold tracking-snug text-ink">
                        {you ? 'You' : (r?.displayName ?? 'Player')}
                      </div>
                      <div className="text-[11px] font-medium text-ink2">{sub}</div>
                    </div>
                  </button>
                  {owner ? (
                    <span className="rounded-[6px] bg-gradient-to-br from-gold-top to-gold-bottom px-2 py-[3px] text-[10px] font-extrabold tracking-wide text-gold-ink">
                      ADMIN
                    </span>
                  ) : isOwner ? (
                    <button
                      type="button"
                      aria-label="Member options"
                      onClick={() => {
                        impact('light');
                        setMenuFor((m) => (m === memberId ? null : memberId));
                      }}
                      className="tap grid h-8 w-8 place-items-center rounded-full text-ink4"
                    >
                      <MoreHorizontal width={18} height={18} strokeWidth={2} />
                    </button>
                  ) : null}
                </div>
                {menuFor === memberId && isOwner && !owner && (
                  <div className="px-[14px] pb-3">
                    <button
                      type="button"
                      disabled={kick.isPending}
                      onClick={() => {
                        impact('warning');
                        kick.mutate(
                          { miniLeagueId: mini.miniLeagueId, userId: memberId },
                          { onSuccess: () => setMenuFor(null) }
                        );
                      }}
                      className="tap flex w-full items-center justify-center gap-2 rounded-[12px] bg-danger/10 py-2.5 text-[13px] font-bold text-danger"
                    >
                      <Trash2 width={15} height={15} strokeWidth={2.2} />
                      Remove {r?.displayName ?? 'member'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {ordered.length > 4 && (
            <>
              <div className="ml-[62px] h-px bg-black/5" />
              <button
                type="button"
                onClick={() => {
                  impact('light');
                  setShowAll((v) => !v);
                }}
                className="tap flex w-full items-center justify-center gap-1.5 py-[13px] text-[13px] font-bold text-accent-ink"
              >
                {showAll ? 'Show less' : `Show all ${ordered.length}`}
                <ChevronDown width={14} height={14} strokeWidth={2.4} className={cn(showAll && 'rotate-180')} />
              </button>
            </>
          )}
        </div>
            </>
          }
          aside={
            <>
        {/* settings (owner only) */}
        {isOwner && (
          <>
            <div className="pb-2 pt-6">
              <span className="text-[13px] font-bold tracking-snug text-ink">League settings</span>
            </div>

            {(editName || editLook) && (
              <div className="mb-2.5 rounded-[18px] border border-black/7 bg-card p-4">
                {editName && (
                  <div>
                    <label className="text-[12px] font-bold uppercase tracking-wide text-ink2">Rename league</label>
                    <div className="mt-2 flex gap-2">
                      <input
                        autoFocus
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value.slice(0, CONFIG.MINI_LEAGUE.NAME_MAX_LENGTH))}
                        className="h-11 flex-1 rounded-[13px] border border-black/10 bg-card px-4 text-[14px] font-bold text-ink outline-none focus:border-accent"
                      />
                      <button
                        type="button"
                        disabled={rename.isPending || !nameDraft.trim() || !!profanityError(nameDraft, 'league name')}
                        onClick={saveName}
                        className="tap rounded-[13px] bg-night px-5 text-[14px] font-extrabold text-white disabled:opacity-60"
                      >
                        {rename.isPending ? '…' : 'Save'}
                      </button>
                    </div>
                    {profanityError(nameDraft, 'league name') && (
                      <p className="mt-2 text-[13px] font-semibold text-danger">
                        {profanityError(nameDraft, 'league name')}
                      </p>
                    )}
                  </div>
                )}
                {editLook && (
                  <div>
                    <label className="text-[12px] font-bold uppercase tracking-wide text-ink2">Icon</label>
                    <IconPicker value={iconDraft} onChange={setIconDraft} />
                    <label className="mt-4 block text-[12px] font-bold uppercase tracking-wide text-ink2">Colour</label>
                    <ColorPicker value={colorDraft} onChange={setColorDraft} />
                    <button
                      type="button"
                      disabled={update.isPending}
                      onClick={saveLook}
                      className="tap mt-4 w-full rounded-[13px] bg-night py-3 text-center text-[14px] font-extrabold text-white disabled:opacity-60"
                    >
                      {update.isPending ? 'Saving…' : 'Save look'}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="overflow-hidden rounded-[20px] border border-black/7 bg-card">
              <SettingRow icon={<Pencil width={19} height={19} strokeWidth={2} />} label="Rename league" onClick={startRename} />
              <div className="ml-[46px] h-px bg-black/5" />
              <SettingRow icon={<Palette width={19} height={19} strokeWidth={2} />} label="Change icon & colour" onClick={startLook} />
            </div>
          </>
        )}

        {/* danger zone */}
        <div className="pt-4">
          {isOwner ? (
            <button
              type="button"
              disabled={del.isPending}
              onClick={onDelete}
              className="tap flex w-full items-center justify-center gap-2 rounded-[16px] border border-danger/25 bg-danger/[0.06] py-[15px] text-[14px] font-bold tracking-snug text-danger disabled:opacity-60"
            >
              <Trash2 width={17} height={17} strokeWidth={2.2} />
              Delete league
            </button>
          ) : (
            <button
              type="button"
              disabled={leave.isPending}
              onClick={onLeave}
              className="tap flex w-full items-center justify-center gap-2 rounded-[16px] border border-danger/25 bg-danger/[0.06] py-[15px] text-[14px] font-bold tracking-snug text-danger disabled:opacity-60"
            >
              <LogOut width={17} height={17} strokeWidth={2.2} />
              Leave league
            </button>
          )}
        </div>
            </>
          }
        />
      </Content>
    </AppShell>
  );
}

function SettingRow({
  icon,
  label,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className="tap flex w-full items-center gap-3 p-[14px_15px] text-left">
      <span className="text-[#52525A]">{icon}</span>
      <span className="flex-1 text-[14px] font-semibold text-ink">{label}</span>
      <ChevronRight width={18} height={18} strokeWidth={2.4} className="text-ink4" />
    </button>
  );
}
